import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Wrench } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Card, Button, Field, Input, Select, Textarea } from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import { PhotoCaptureField } from "@/features/assets/PhotoCaptureField";
import { uploadAssetPhoto } from "@/features/assets/assetPhotoUpload";

// =============================================================================
// HandToolNewScreen — /assets/hand-tools/new
// Create form for a hand tool. Open to any authenticated user so crew can
// self-register gear bought on the road. Optional phone-camera photo
// uploaded post-insert. T-051 Part B.
// =============================================================================

const CATEGORIES  = ["Hand Tool", "Measuring", "Other"] as const;
const CONDITIONS  = ["new", "good", "fair", "poor", "decommissioned"] as const;

export default function HandToolNewScreen() {
  useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // T-253 — tool_code is system-generated by the DB default (mig 145).
  const [asset_tag, setAssetTag]       = useState("");
  const [display_name, setDisplayName] = useState("");
  const [tool_category, setCategory]   = useState<string>("Hand Tool");
  const [tool_type, setToolType]       = useState("");
  const [size_spec, setSizeSpec]       = useState("");
  const [make, setMake]                = useState("");
  const [model, setModel]              = useState("");
  const [serial_number, setSerial]     = useState("");
  const [quantity, setQuantity]        = useState("1");
  const [purchase_date, setPurchaseDate]   = useState("");
  const [purchase_price, setPurchasePrice] = useState("");
  const [purchase_supplier, setSupplier]   = useState("");
  const [condition, setCondition]          = useState<string>("good");
  const [kit_name, setKitName]             = useState("");
  const [assigned_to_employee_id, setAssignedTo] = useState("");
  const [notes, setNotes]                  = useState("");
  const [photo, setPhoto]                  = useState<File | null>(null);
  const [errMsg, setErrMsg]                = useState<string | null>(null);

  const { data: employees = [] } = useQuery({
    queryKey: ["employees_active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("employee_id, first_name, last_name")
        .eq("is_active", true)
        .order("first_name");
      if (error) throw error;
      return data ?? [];
    },
  });

  const createMut = useMutation({
    mutationFn: async () => {
      // tool_code intentionally omitted — DB default mints it (T-253, mig 145)
      if (!display_name.trim()) throw new Error("Display name is required.");
      const { data, error } = await supabase
        .from("hand_tools")
        .insert({
          // tool_code: omitted — DB default mints it (mig 145)
          asset_tag: asset_tag.trim() || null, // mig 145 trigger defaults to tool_code if null
          display_name: display_name.trim(),
          tool_category: tool_category || null,
          tool_type: tool_type.trim() || null,
          size_spec: size_spec.trim() || null,
          make: make.trim() || null,
          model: model.trim() || null,
          serial_number: serial_number.trim() || null,
          quantity: parseInt(quantity, 10) || 1,
          purchase_date: purchase_date || null,
          purchase_price: purchase_price ? parseFloat(purchase_price) : null,
          purchase_supplier: purchase_supplier.trim() || null,
          condition: condition || null,
          kit_name: kit_name.trim() || null,
          assigned_to_employee_id: assigned_to_employee_id || null,
          notes: notes.trim() || null,
          is_active: true,
        })
        .select("tool_id")
        .single();
      if (error) throw error;
      const newId = data.tool_id as string;
      if (photo) {
        try { await uploadAssetPhoto("hand_tool", newId, photo); }
        catch (e) { console.warn("Hand tool photo upload failed:", e); }
      }
      return newId;
    },
    onSuccess: (toolId) => {
      qc.invalidateQueries({ queryKey: ["assets"] });
      navigate(`/assets/hand-tools/${toolId}`);
    },
    onError: (e: unknown) => setErrMsg(e instanceof Error ? e.message : "Create failed"),
  });

  const canSubmit = !!display_name.trim() && !createMut.isPending;

  return (
    <div className="p-6 max-w-3xl mx-auto pb-28">
      <button onClick={() => navigate("/assets")} className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3" type="button">
        <ArrowLeft size={14} /> Back to assets
      </button>
      <h1 className="text-[20px] font-semibold text-text flex items-center gap-2 mb-5">
        <Wrench size={20} className="text-accent" /> New hand tool
      </h1>

      <Card>
        <div className="p-5 border-b border-line">
          <PhotoCaptureField
            value={photo}
            onChange={setPhoto}
            label="Reference photo"
            hint="Quick snap — useful reference in the register."
          />
        </div>
        <div className="p-5 border-b border-line">
          <div className="text-[13px] font-medium text-text mb-3">Identity</div>
          {/* T-253 — code auto-generated by the DB on save (HND-NNNNN). */}
          <div className="mb-3 px-3 py-2 rounded bg-accent/5 border border-accent/20 text-[12px] text-text-muted">
            <span className="font-mono text-accent">HND-NNNNN</span> code generated automatically on save. No need to type one.
          </div>
          <Field label="Asset tag" hint="Physical sticker number. Defaults to the auto-generated code if blank.">
            <Input mono value={asset_tag} onChange={e => setAssetTag(e.target.value)} autoFocus />
          </Field>
          <Field label="Display name *">
            <Input value={display_name} onChange={e => setDisplayName(e.target.value)} placeholder="e.g. 24mm Combination Wrench" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={tool_category} onChange={e => setCategory(e.target.value)}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label="Type" hint="Free text, e.g. Wrench, Pliers, Hammer.">
              <Input value={tool_type} onChange={e => setToolType(e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Size / spec">
              <Input value={size_spec} onChange={e => setSizeSpec(e.target.value)} placeholder='e.g. 24mm'/>
            </Field>
            <Field label="Make">
              <Input value={make} onChange={e => setMake(e.target.value)} />
            </Field>
            <Field label="Model">
              <Input value={model} onChange={e => setModel(e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Serial number">
              <Input mono value={serial_number} onChange={e => setSerial(e.target.value)} />
            </Field>
            <Field label="Quantity">
              <Input type="number" mono value={quantity} onChange={e => setQuantity(e.target.value)} />
            </Field>
          </div>
        </div>

        <div className="p-5 border-b border-line">
          <div className="text-[13px] font-medium text-text mb-3">Lifecycle</div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Purchase date">
              <Input type="date" mono value={purchase_date} onChange={e => setPurchaseDate(e.target.value)} />
            </Field>
            <Field label="Price">
              <Input type="number" mono value={purchase_price} onChange={e => setPurchasePrice(e.target.value)} placeholder="ex-GST" />
            </Field>
            <Field label="Supplier">
              <Input value={purchase_supplier} onChange={e => setSupplier(e.target.value)} />
            </Field>
          </div>
          <Field label="Condition">
            <Select value={condition} onChange={e => setCondition(e.target.value)}>
              {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
          </Field>
        </div>

        <div className="p-5">
          <div className="text-[13px] font-medium text-text mb-3">Assignment &amp; notes</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Kit / allocation">
              <Input value={kit_name} onChange={e => setKitName(e.target.value)} placeholder="e.g. Rigging Kit 1" />
            </Field>
            <Field label="Assigned to">
              <Select value={assigned_to_employee_id} onChange={e => setAssignedTo(e.target.value)}>
                <option value="">— Pool —</option>
                {employees.map(e => {
                  const name = `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim();
                  return <option key={e.employee_id} value={e.employee_id}>{name}</option>;
                })}
              </Select>
            </Field>
          </div>
          <Field label="Notes">
            <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
          </Field>
        </div>
      </Card>

      <StickyBottomBar>
        {errMsg && (
          <div className="px-3 py-2 bg-red-900/30 border-b border-red-900/50 text-red-200 text-[12px] text-center">{errMsg}</div>
        )}
        <div className="p-3 flex justify-center">
          <div className="flex gap-2 max-w-2xl w-full">
            <Button variant="secondary" size="lg" onClick={() => navigate("/assets")} type="button">Cancel</Button>
            <Button size="lg" icon={CheckCircle2} onClick={() => createMut.mutate()} disabled={!canSubmit} className="flex-1 justify-center" type="button">
              {createMut.isPending ? "Creating…" : "Create hand tool"}
            </Button>
          </div>
        </div>
      </StickyBottomBar>
    </div>
  );
}
